import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { StatService } from '@cargos/sprintpay-services';
import { MaintenanceShellComponent } from '@cargos/sprintpay-ui';
import { map, Observable, of, startWith } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Facility-side wrapper around `MaintenanceShellComponent` that binds the
 * Statsig feature gate and dynamic-config copy declared in
 * `environment.maintenance`. Each facility root template only needs to project
 * its layout inside `<app-maintenance-gate>...</app-maintenance-gate>` — no
 * per-facility wiring required. When the gate is ON, the maintenance overlay
 * replaces the projected content instantly (no reload / redeploy).
 */
@Component({
    standalone: true,
    selector: 'app-maintenance-gate',
    template: `
        @let copy = copy$ | async;
        <lib-maintenance-shell
            [maintenance$]="maintenance$"
            logoSrc="./assets/img/logo-cargosprint-horizontal-color.webp"
            logoAlt="CargoSprint"
            [badgeLabel]="copy?.badgeLabel ?? 'Scheduled Maintenance'"
            [title]="copy?.title ?? ''"
            [appName]="copy?.appName ?? ''"
            [message]="copy?.message ?? ''"
            [dateValue]="copy?.dateValue ?? ''"
            [statusValue]="copy?.statusValue ?? ''"
            [returnValue]="copy?.returnValue ?? ''"
            [footerText]="copy?.footerText ?? ''"
            [contactUrl]="copy?.contactUrl ?? ''"
        >
            <ng-content></ng-content>
        </lib-maintenance-shell>
    `,
    imports: [CommonModule, AsyncPipe, MaintenanceShellComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaintenanceGateComponent {
    private readonly statService = inject(StatService, { optional: true });

    private static readonly TITLE_FALLBACK = "We'll Be Back Soon";
    private static readonly MESSAGE_FALLBACK =
        'This site is currently unavailable due to scheduled maintenance. Our team is working to restore service as quickly as possible.';
    private static readonly APP_NAME_FALLBACK = 'CargoSprint';
    private static readonly STATUS_FALLBACK = 'Planned system maintenance is currently in progress.';
    private static readonly FOOTER_FALLBACK = 'Thank you for your patience and understanding.';
    private static readonly CONTACT_URL_FALLBACK = 'https://cargosprint.com/contact/';

    private readonly gateName: string = environment.maintenance?.gateName ?? 'maintenance_mode';
    private readonly dynamicConfigName: string =
        environment.maintenance?.dynamicConfigName ?? 'maintenance_mode_config';

    public readonly maintenance$: Observable<boolean> = (
        this.statService?.isFeatureEnabledWhenReady(this.gateName) ?? of(false)
    ).pipe(startWith(false));

    /**
     * Copy is read *after* the SDK is ready (isFeatureEnabledWhenReady awaits
     * waitUntilReady before emitting). Reading it synchronously in a field
     * initializer would return the fallback because the Dynamic Config isn't
     * loaded yet.
     */
    public readonly copy$: Observable<{
        title: string;
        appName: string;
        message: string;
        badgeLabel: string;
        dateValue: string;
        statusValue: string;
        returnValue: string;
        footerText: string;
        contactUrl: string;
    }> = this.maintenance$.pipe(
        map(() => {
            const read = <T>(key: string, fallback: T): T =>
                (this.statService?.getDynamicConfig(this.dynamicConfigName).get<T>(key, fallback) as T) ?? fallback;

            return {
                title: read('title', MaintenanceGateComponent.TITLE_FALLBACK),
                appName: read('appName', MaintenanceGateComponent.APP_NAME_FALLBACK),
                message: read('message', MaintenanceGateComponent.MESSAGE_FALLBACK),
                badgeLabel: read('badgeLabel', 'Scheduled Maintenance'),
                dateValue: read('dateValue', ''),
                statusValue: read('statusValue', MaintenanceGateComponent.STATUS_FALLBACK),
                returnValue: read('returnValue', ''),
                footerText: read('footerText', MaintenanceGateComponent.FOOTER_FALLBACK),
                contactUrl: read('contactUrl', MaintenanceGateComponent.CONTACT_URL_FALLBACK),
            };
        })
    );
}
